/**
 * BeezFy Stealth Footer 2025 - Premium JavaScript
 * Mobile-first interactions, smooth animations, performance optimized
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================
  const CONFIG = {
    mobileBreakpoint: 768,
    scrollThreshold: 100,
    animationDuration: 400,
    debounceDelay: 150,
    localStorageKey: 'beezfy_footer_prefs'
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  const utils = {
    // Debounce function for performance
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    // Check if mobile
    isMobile() {
      return window.innerWidth <= CONFIG.mobileBreakpoint;
    },

    // Smooth scroll with easing
    smoothScroll(target, duration = 500) {
      const targetElement = document.querySelector(target);
      if (!targetElement) return;

      const targetPosition = targetElement.getBoundingClientRect().top;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      let startTime = null;

      function animation(currentTime) {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const run = easeInOutQuad(timeElapsed, startPosition, distance, duration);
        window.scrollTo(0, run);
        if (timeElapsed < duration) requestAnimationFrame(animation);
      }

      function easeInOutQuad(t, b, c, d) {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t + b;
        t--;
        return -c / 2 * (t * (t - 2) - 1) + b;
      }

      requestAnimationFrame(animation);
    },

    // Local storage helpers
    storage: {
      get(key) {
        try {
          const item = localStorage.getItem(`${CONFIG.localStorageKey}_${key}`);
          return item ? JSON.parse(item) : null;
        } catch (e) {
          return null;
        }
      },
      set(key, value) {
        try {
          localStorage.setItem(`${CONFIG.localStorageKey}_${key}`, JSON.stringify(value));
        } catch (e) {
          // Silently fail
        }
      }
    }
  };

  // ============================================
  // STEALTH FOOTER CLASS
  // ============================================
  class StealthFooter {
    constructor() {
      this.footer = document.querySelector('.stealth-footer');
      this.stickyBar = document.querySelector('.stealth-footer-bar');
      this.accordionColumns = [];
      this.newsletterForm = null;
      this.lastScrollY = 0;
      this.isInitialized = false;
      
      this.init();
    }

    init() {
      if (this.isInitialized || !this.footer) return;

      this.setupAccordion();
      this.setupStickyBar();
      this.setupNewsletter();
      this.setupAnimations();
      this.loadUserPreferences();
      this.setupBeezfyIntegration(); // Integrazione eventi BeezFy

      this.isInitialized = true;
    }

    // ============================================
    // BEEZFY SYSTEMS INTEGRATION
    // ============================================
    setupBeezfyIntegration() {
      // Listen per eventi cart update
      document.addEventListener('beezfy:cart:updated', (e) => {
        this.updateBadges();
      });

      // Listen per eventi wishlist update
      document.addEventListener('beezfy:wishlist:updated', (e) => {
        this.updateBadges();
      });

      // Listen per storage changes (multi-tab sync)
      window.addEventListener('storage', (e) => {
        if (e.key === 'beezfy-wishlist' || e.key?.includes('cart')) {
          this.updateBadges();
        }
      });

      // Aggiorna badges iniziali
      if (this.stickyBar) {
        setTimeout(() => this.updateBadges(), 500);
      }
    }

    // ============================================
    // ACCORDION FUNCTIONALITY (Mobile)
    // ============================================
    setupAccordion() {
      if (!utils.isMobile()) return;

      const columns = this.footer.querySelectorAll('.stealth-footer__column');
      
      columns.forEach((column, index) => {
        const header = column.querySelector('.stealth-footer__column-header');
        const content = column.querySelector('.stealth-footer__column-content');
        
        if (!header || !content) return;

        // Add arrow icon if not present
        if (!header.querySelector('.stealth-footer__column-arrow')) {
          const arrow = document.createElement('span');
          arrow.className = 'stealth-footer__column-arrow';
          arrow.innerHTML = `
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor">
              <path d="M2 4l4 4 4-4" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          `;
          header.appendChild(arrow);
        }

        // Toggle functionality
        header.addEventListener('click', () => {
          this.toggleAccordion(column, columns);
        });

        // Store reference
        this.accordionColumns.push({ column, header, content });
      });

      // Open first column by default on mobile
      if (this.accordionColumns.length > 0 && utils.isMobile()) {
        const savedState = utils.storage.get('accordion_state');
        if (savedState === null) {
          this.accordionColumns[0].column.classList.add('is-open');
        } else if (savedState >= 0 && savedState < this.accordionColumns.length) {
          this.accordionColumns[savedState].column.classList.add('is-open');
        }
      }
    }

    toggleAccordion(targetColumn, allColumns) {
      const isOpen = targetColumn.classList.contains('is-open');
      
      // Close all columns
      allColumns.forEach(col => {
        col.classList.remove('is-open');
      });
      
      // Open target if it was closed
      if (!isOpen) {
        targetColumn.classList.add('is-open');
        
        // Save state
        const index = Array.from(allColumns).indexOf(targetColumn);
        utils.storage.set('accordion_state', index);
      } else {
        utils.storage.set('accordion_state', -1);
      }
    }

    // ============================================
    // STICKY BAR FUNCTIONALITY - PREMIUM UX
    // ============================================
    setupStickyBar() {
      if (!this.stickyBar) return;

      // Get admin settings from data attributes
      const scrollBehavior = this.stickyBar.dataset.scrollBehavior || 'hide_on_scroll_down';
      const scrollThreshold = parseInt(this.stickyBar.dataset.scrollThreshold) || 100;

      // Setup scroll behavior based on admin settings
      if (scrollBehavior === 'hide_on_scroll_down') {
        this.setupHideOnScrollDown(scrollThreshold);
      } else if (scrollBehavior === 'auto_hide') {
        this.setupAutoHide();
      }
      // 'always_visible' doesn't need setup - it's already visible

      // Setup click handlers for sticky bar items
      const barItems = this.stickyBar.querySelectorAll('.stealth-footer-bar__item');
      barItems.forEach(item => {
        item.addEventListener('click', (e) => {
          this.handleStickyBarClick(e, item);
        });

        // Add touch feedback
        item.addEventListener('touchstart', () => {
          item.style.transform = 'scale(0.92)';
        }, { passive: true });

        item.addEventListener('touchend', () => {
          setTimeout(() => {
            item.style.transform = '';
          }, 150);
        }, { passive: true });
      });

      // Elevated shadow when page scrolled
      window.addEventListener('scroll', utils.debounce(() => {
        if (window.pageYOffset > 50) {
          this.stickyBar.classList.add('stealth-footer-bar--elevated');
        } else {
          this.stickyBar.classList.remove('stealth-footer-bar--elevated');
        }
      }, 50), { passive: true });

      // Update cart/wishlist badges
      this.updateBadges();
    }

    setupHideOnScrollDown(threshold) {
      let lastScrollTop = 0;
      let scrollTimeout;

      const scrollHandler = utils.debounce(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        // Clear any existing timeout
        clearTimeout(scrollTimeout);

        if (scrollTop > lastScrollTop && scrollTop > threshold) {
          // Scrolling down - hide bar
          this.stickyBar.classList.add('stealth-footer-bar--hidden');
        } else if (scrollTop < lastScrollTop) {
          // Scrolling up - show bar
          this.stickyBar.classList.remove('stealth-footer-bar--hidden');
        }

        lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
      }, 100);

      window.addEventListener('scroll', scrollHandler, { passive: true });
    }

    setupAutoHide() {
      let hideTimeout;
      let isUserInteracting = false;

      const hideBar = () => {
        if (!isUserInteracting) {
          this.stickyBar.classList.add('stealth-footer-bar--hidden');
        }
      };

      const showBar = () => {
        this.stickyBar.classList.remove('stealth-footer-bar--hidden');
        clearTimeout(hideTimeout);
        hideTimeout = setTimeout(hideBar, 3000);
      };

      // Show on scroll
      window.addEventListener('scroll', utils.debounce(showBar, 100), { passive: true });

      // Show on touch
      this.stickyBar.addEventListener('touchstart', () => {
        isUserInteracting = true;
        showBar();
      }, { passive: true });

      this.stickyBar.addEventListener('touchend', () => {
        setTimeout(() => {
          isUserInteracting = false;
        }, 1000);
      }, { passive: true });

      // Initial hide
      hideTimeout = setTimeout(hideBar, 3000);
    }

    handleStickyBarClick(e, item) {
      const action = item.dataset.action;

      switch(action) {
        case 'link':
          // Link handled by <a> tag, do nothing
          break;
        case 'search':
          this.openSearch();
          break;
        case 'wishlist':
          this.openWishlist();
          break;
        case 'cart':
          this.openCart();
          break;
        case 'menu':
          this.toggleMobileMenu();
          break;
        default:
          // For any other action, try to find and trigger existing elements
          console.log('Mobile bar action:', action);
          break;
      }

      // Add active state
      document.querySelectorAll('.stealth-footer-bar__item').forEach(el => {
        el.classList.remove('stealth-footer-bar__item--active');
      });
      item.classList.add('stealth-footer-bar__item--active');
    }

    openSearch() {
      // Try multiple search integration methods
      if (window.BeezfyDrawerSystem && typeof window.BeezfyDrawerSystem.openDrawer === 'function') {
        window.BeezfyDrawerSystem.openDrawer('search');
      } else if (document.querySelector('[data-drawer-trigger="search"]')) {
        document.querySelector('[data-drawer-trigger="search"]').click();
      } else if (document.querySelector('.header__search')) {
        document.querySelector('.header__search').click();
      } else if (document.querySelector('details-modal[id*="search"]')) {
        document.querySelector('details-modal[id*="search"]').querySelector('summary').click();
      } else {
        // Fallback: try to find any search input and focus it
        const searchInput = document.querySelector('input[type="search"], input[name="q"]');
        if (searchInput) {
          searchInput.focus();
        }
      }
    }

    updateBadges() {
      // Update cart badge with animation
      const cartBadge = this.stickyBar?.querySelector('[data-action="cart"] .stealth-footer-bar__badge');
      if (cartBadge) {
        const cartCount = this.getCartCount();
        const previousCount = parseInt(cartBadge.textContent) || 0;

        if (cartCount > 0) {
          const displayCount = cartCount > 99 ? '99+' : cartCount;
          cartBadge.textContent = displayCount;
          cartBadge.style.display = 'flex';

          // Add/remove double-digit class for smaller font (prevent badge distortion)
          if (cartCount >= 10) {
            cartBadge.classList.add('badge-double-digit');
          } else {
            cartBadge.classList.remove('badge-double-digit');
          }

          // Add pop-in animation if count increased
          if (cartCount > previousCount) {
            cartBadge.classList.remove('badge-appear');
            void cartBadge.offsetWidth; // Force reflow
            cartBadge.classList.add('badge-appear');
          }
        } else {
          cartBadge.style.display = 'none';
          cartBadge.classList.remove('badge-double-digit');
        }
      }

      // Update wishlist badge with animation
      const wishlistBadge = this.stickyBar?.querySelector('[data-action="wishlist"] .stealth-footer-bar__badge');
      if (wishlistBadge) {
        const wishlistCount = this.getWishlistCount();
        const previousCount = parseInt(wishlistBadge.textContent) || 0;

        if (wishlistCount > 0) {
          wishlistBadge.textContent = wishlistCount;
          wishlistBadge.style.display = 'flex';

          // Add/remove double-digit class for smaller font (prevent badge distortion)
          if (wishlistCount >= 10) {
            wishlistBadge.classList.add('badge-double-digit');
          } else {
            wishlistBadge.classList.remove('badge-double-digit');
          }

          // Add pop-in animation if count increased
          if (wishlistCount > previousCount) {
            wishlistBadge.classList.remove('badge-appear');
            void wishlistBadge.offsetWidth; // Force reflow
            wishlistBadge.classList.add('badge-appear');
          }
        } else {
          wishlistBadge.style.display = 'none';
          wishlistBadge.classList.remove('badge-double-digit');
        }
      }
    }

    getCartCount() {
      // Integrazione con BeezFy Cart System
      if (window.BeezfyCart && window.BeezfyCart.cart) {
        return window.BeezfyCart.cart.item_count || 0;
      }
      // Fallback: cerca badge cart esistente
      const cartCount = document.querySelector('[data-cart-count], .cart-count-bubble, #cart-icon-bubble');
      return parseInt(cartCount?.textContent || '0');
    }

    getWishlistCount() {
      // Integrazione con BeezFy Wishlist System
      if (window.BeezfyWishlist && typeof window.BeezfyWishlist.getCount === 'function') {
        return window.BeezfyWishlist.getCount();
      }
      // Fallback: localStorage
      const wishlistKey = 'beezfy-wishlist';
      try {
        const wishlist = JSON.parse(localStorage.getItem(wishlistKey) || '[]');
        return Array.isArray(wishlist) ? wishlist.length : 0;
      } catch(e) {
        return 0;
      }
    }

    openCart() {
      // Integrazione con BeezFy Drawer System
      if (window.BeezfyDrawerSystem && typeof window.BeezfyDrawerSystem.openDrawer === 'function') {
        window.BeezfyDrawerSystem.openDrawer('cart');
      } else if (window.openDrawer && typeof window.openDrawer === 'function') {
        window.openDrawer();
      } else if (document.querySelector('[data-drawer-trigger="cart"]')) {
        document.querySelector('[data-drawer-trigger="cart"]').click();
      } else {
        // Fallback: vai a pagina cart
        window.location.href = '/cart';
      }
    }

    openWishlist() {
      // Integrazione con BeezFy Drawer System
      if (window.BeezfyDrawerSystem && typeof window.BeezfyDrawerSystem.openDrawer === 'function') {
        window.BeezfyDrawerSystem.openDrawer('wishlist');
      } else if (window.BeezfyWishlistDrawer && typeof window.BeezfyWishlistDrawer.open === 'function') {
        window.BeezfyWishlistDrawer.open();
      } else if (document.querySelector('[data-drawer-trigger="wishlist"]')) {
        document.querySelector('[data-drawer-trigger="wishlist"]').click();
      } else {
        // Fallback: vai a pagina wishlist
        window.location.href = '/pages/wishlist';
      }
    }

    toggleMobileMenu() {
      // Integrazione con BeezFy Mobile Menu System
      if (window.BeezfyDrawerSystem && typeof window.BeezfyDrawerSystem.openDrawer === 'function') {
        window.BeezfyDrawerSystem.openDrawer('menu');
      } else if (window.BeezfyMobileMenu && typeof window.BeezfyMobileMenu.toggle === 'function') {
        window.BeezfyMobileMenu.toggle();
      } else if (document.querySelector('[data-drawer-trigger="menu"]')) {
        document.querySelector('[data-drawer-trigger="menu"]').click();
      } else {
        // Fallback: toggle classe body
        document.body.classList.toggle('mobile-menu-open');
      }
    }

    // ============================================
    // NEWSLETTER FUNCTIONALITY
    // ============================================
    setupNewsletter() {
      this.newsletterForm = this.footer.querySelector('.stealth-footer__newsletter-form');
      if (!this.newsletterForm) return;

      this.newsletterForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleNewsletterSubmit();
      });

      // Input animation
      const input = this.newsletterForm.querySelector('.stealth-footer__newsletter-input');
      if (input) {
        input.addEventListener('focus', () => {
          this.newsletterForm.classList.add('is-focused');
        });
        
        input.addEventListener('blur', () => {
          this.newsletterForm.classList.remove('is-focused');
        });
      }
    }

    async handleNewsletterSubmit() {
      const input = this.newsletterForm.querySelector('.stealth-footer__newsletter-input');
      const button = this.newsletterForm.querySelector('.stealth-footer__newsletter-button');
      const email = input?.value;

      if (!email || !this.validateEmail(email)) {
        this.showNewsletterError('Please enter a valid email address');
        return;
      }

      // Show loading state
      button.textContent = 'Subscribing...';
      button.disabled = true;

      try {
        // Make API call to Shopify or your newsletter service
        const response = await fetch('/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            form_type: 'customer',
            utf8: '✓',
            contact: {
              email: email,
              accepts_marketing: true
            }
          })
        });

        if (response.ok) {
          this.showNewsletterSuccess();
          input.value = '';
        } else {
          throw new Error('Subscription failed');
        }
      } catch (error) {
        this.showNewsletterError('Something went wrong. Please try again.');
      } finally {
        button.textContent = 'Subscribe';
        button.disabled = false;
      }
    }

    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    }

    showNewsletterSuccess() {
      this.showNewsletterMessage('Thank you for subscribing!', 'success');
    }

    showNewsletterError(message) {
      this.showNewsletterMessage(message, 'error');
    }

    showNewsletterMessage(message, type) {
      const messageEl = document.createElement('div');
      messageEl.className = `stealth-footer__newsletter-message stealth-footer__newsletter-message--${type}`;
      messageEl.textContent = message;
      
      this.newsletterForm.appendChild(messageEl);
      
      setTimeout(() => {
        messageEl.classList.add('is-visible');
      }, 10);
      
      setTimeout(() => {
        messageEl.classList.remove('is-visible');
        setTimeout(() => {
          messageEl.remove();
        }, 300);
      }, 3000);
    }

    // ============================================
    // THEME - Removed toggle button, theme controlled via admin only
    // ============================================

    // ============================================
    // ANIMATIONS & INTERACTIONS
    // ============================================
    setupAnimations() {
      // Intersection Observer for fade-in animations
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      // Observe elements
      const animatedElements = this.footer.querySelectorAll('.stealth-footer__column, .stealth-footer__trust, .stealth-footer__newsletter');
      animatedElements.forEach(el => {
        observer.observe(el);
      });

      // Parallax effect for desktop
      if (!utils.isMobile()) {
        window.addEventListener('scroll', utils.debounce(() => {
          const scrolled = window.pageYOffset;
          const parallaxElements = this.footer.querySelectorAll('[data-parallax]');
          
          parallaxElements.forEach(el => {
            const speed = el.dataset.parallax || 0.5;
            const yPos = -(scrolled * speed);
            el.style.transform = `translateY(${yPos}px)`;
          });
        }, 10));
      }
    }

    // ============================================
    // USER PREFERENCES
    // ============================================
    loadUserPreferences() {
      // Load and apply saved preferences
      const preferences = {
        theme: utils.storage.get('theme'),
        newsletter_dismissed: utils.storage.get('newsletter_dismissed'),
        accordion_state: utils.storage.get('accordion_state')
      };

      // Apply preferences
      if (preferences.newsletter_dismissed) {
        const newsletter = this.footer.querySelector('.stealth-footer__newsletter');
        if (newsletter) {
          newsletter.style.display = 'none';
        }
      }
    }

    // ============================================
    // PUBLIC API
    // ============================================
    destroy() {
      // Clean up event listeners and observers
      this.isInitialized = false;
    }

    refresh() {
      this.destroy();
      this.init();
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================
  document.addEventListener('DOMContentLoaded', () => {
    window.StealthFooter = new StealthFooter();

    // Reinitialize on theme change
    document.addEventListener('shopify:theme:change', () => {
      window.StealthFooter.refresh();
    });

    // Handle resize events
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        window.StealthFooter.refresh();
      }, 250);
    });
  });

  // Export for use in other scripts
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = StealthFooter;
  }
})();
